import axios from 'axios';
import { API_URL } from '../config';
import api from './api';
import type {
  AgendaKind,
  AgendaLayoutKey,
  ChangeAgendaPreview,
  ChangeAgendaEmailSchedule,
  ChangeAgendaEmailTemplate,
  ChangeAgendaEmailVariable,
  ChangeAgendaRecipient,
  ChangeAgendaRecipientCommittee,
  ChangeAgendaSend,
  ChangeAgendaSendSummary,
  ChangeMeetingAgendaSendSummary,
  ChangeEmailTriggerConfig,
  ChangeEmailTriggerQueuedItem,
  ChangeEmailQueueItem,
  ChangeApprovalSettings,
  ChangeAssistantHistoryMessage,
  ChangeAssistantResponse,
  ChangeCommittee,
  ChangeCommitteePerson,
  ChangeCabSlide,
  ChangeCabSlideField,
  ChangeCabDeck,
  ChangeCabDeckTheme,
  ChangeCalendarHoliday,
  ChangeCalendarItem,
  ChangeMeeting,
  ChangeMeetingAgendaEmailPreview,
  ChangeMeetingKind,
  ChangeMeetingLite,
  ChangeCatalogItem,
  ChangeCatalogKindItem,
  ChangeCatalogPayload,
  ChangeEmergencyPolicy,
  ChangeApprovalFlowConfig,
  ChangeTypeApprovalFlow,
  ChangeTypeApprovalFlowSummary,
  ChangeSapRequestTransportSettings,
  ChangeChecklistGateStatus,
  ChangeExtraValue,
  ChangeFormField,
  ChangeListResponse,
  ChangeRequest,
  RequesterImmediateManagerSetup,
  ChangeSubtaskConcludeMode,
  ChangeSubtaskFormSchemaResponse,
  ChangeTypePolicy,
  ChangeWorkGroup,
  ChangeWorkGroupExportItem,
  ChangeWorkGroupsExportPayload,
  ChangeWorkGroupsImportResponse,
  ChangeDetailTabDefinition,
  ChangeDetailTabEntryDetail,
  ChangeDetailTabEntrySlim,
  ChangeDetailTabField,
  ChangeDetailPageTabLayoutResponse,
} from '../types/changes';

interface ListParams {
  skip?: number;
  limit?: number;
  search?: string;
  status?: string;
  change_type?: string;
  category_id?: number;
  project?: string;
  owner_id?: number;
  date_from?: string;
  date_to?: string;
  date_scope?: 'any' | 'go_live' | 'riscos' | 'camud';
}

/** Cada cadastro é uma coleção REST própria sob /api/changes. */
export type ChangeCatalogKind = 'categories' | 'requester-areas' | 'task-types';

export const changesApi = {
  list: async (params: ListParams = {}): Promise<ChangeListResponse> => {
    const response = await api.get('/api/changes/', { params });
    return response.data as ChangeListResponse;
  },

  getById: async (id: number): Promise<ChangeRequest> => {
    const response = await api.get(`/api/changes/${id}`);
    return response.data as ChangeRequest;
  },

  create: async (payload: Record<string, unknown>): Promise<ChangeRequest> => {
    const response = await api.post('/api/changes/', payload);
    return response.data as ChangeRequest;
  },

  update: async (id: number, payload: Record<string, unknown>): Promise<ChangeRequest> => {
    const response = await api.put(`/api/changes/${id}`, payload);
    return response.data as ChangeRequest;
  },

  updateStatus: async (id: number, status: string): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${id}/status`, { status });
    return response.data as ChangeRequest;
  },

  allowedTransitions: async (
    id: number,
  ): Promise<{
    change_id: number;
    current_status: string;
    change_type: string;
    allowed: string[];
    blocked?: Array<{ status: string; reason: string }>;
  }> => {
    const response = await api.get(`/api/changes/${id}/allowed-transitions`);
    return response.data;
  },

  statuses: async (): Promise<{ items: string[] }> => {
    const response = await api.get('/api/changes/statuses');
    return response.data;
  },

  /** Abas do hub: cadastros simples + especiais. */
  listCatalogRegistry: async (): Promise<{ items: ChangeCatalogKindItem[] }> => {
    const response = await api.get('/api/changes/catalogs');
    return response.data;
  },

  /** Cadastros de apoio (categoria, área solicitante). */
  listCatalog: async (
    kind: ChangeCatalogKind,
    params: { only_active?: boolean } = {},
  ): Promise<{ items: ChangeCatalogItem[] }> => {
    const response = await api.get(`/api/changes/${kind}`, { params });
    return response.data;
  },

  createCatalogItem: async (
    kind: ChangeCatalogKind,
    payload: ChangeCatalogPayload,
  ): Promise<ChangeCatalogItem> => {
    const response = await api.post(`/api/changes/${kind}`, payload);
    return response.data as ChangeCatalogItem;
  },

  updateCatalogItem: async (
    kind: ChangeCatalogKind,
    itemId: number,
    payload: ChangeCatalogPayload,
  ): Promise<ChangeCatalogItem> => {
    const response = await api.put(`/api/changes/${kind}/${itemId}`, payload);
    return response.data as ChangeCatalogItem;
  },

  getFormSchema: async (): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.get('/api/changes/form-schema');
    return response.data;
  },

  createFormField: async (payload: Record<string, unknown>): Promise<ChangeFormField> => {
    const response = await api.post('/api/changes/form-fields', payload);
    return response.data as ChangeFormField;
  },

  updateFormField: async (
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(`/api/changes/form-fields/${fieldId}`, payload);
    return response.data as ChangeFormField;
  },

  deleteFormField: async (fieldId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/form-fields/${fieldId}`);
    return response.data;
  },

  reorderFormFields: async (ids: number[]): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put('/api/changes/form-fields/reorder', { ids });
    return response.data;
  },

  getDetailSummarySchema: async (): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.get('/api/changes/detail-summary-schema');
    return response.data;
  },

  saveDetailSummarySchema: async (payload: {
    items: Array<{ id: number; detail_summary_visible: boolean }>;
  }): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put('/api/changes/detail-summary-schema', payload);
    return response.data;
  },

  getSubtaskFormSchema: async (): Promise<ChangeSubtaskFormSchemaResponse> => {
    const response = await api.get('/api/changes/subtask-form-schema');
    return response.data as ChangeSubtaskFormSchemaResponse;
  },

  updateSubtaskFormSettings: async (payload: {
    conclude_mode: ChangeSubtaskConcludeMode;
    conclude_button_label: string;
    allow_create_attachments?: boolean;
    checklist_required_before_status?: ChangeChecklistGateStatus;
    internal_subtask_gate_before_status?: import('../types/changes').ChangeSubtaskGateStatus;
    external_subtask_gate_before_status?: import('../types/changes').ChangeSubtaskGateStatus;
    requester_default_allow_create?: boolean;
    requester_default_allow_edit?: boolean;
    requester_default_allow_delete?: boolean;
    package_create_statuses?: string[];
  }): Promise<ChangeSubtaskFormSchemaResponse> => {
    const response = await api.put('/api/changes/subtask-form-settings', payload);
    return response.data as ChangeSubtaskFormSchemaResponse;
  },

  createSubtaskFormField: async (payload: Record<string, unknown>): Promise<ChangeFormField> => {
    const response = await api.post('/api/changes/subtask-form-fields', payload);
    return response.data as ChangeFormField;
  },

  updateSubtaskFormField: async (
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(`/api/changes/subtask-form-fields/${fieldId}`, payload);
    return response.data as ChangeFormField;
  },

  deleteSubtaskFormField: async (fieldId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/subtask-form-fields/${fieldId}`);
    return response.data;
  },

  reorderSubtaskFormFields: async (ids: number[]): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put('/api/changes/subtask-form-fields/reorder', { ids });
    return response.data;
  },

  getInternalSubtaskPresets: async (): Promise<
    import('../types/changes').InternalSubtaskPresetField[]
  > => {
    const response = await api.get('/api/changes/internal-subtask-presets');
    return response.data.items;
  },

  createInternalSubtaskPreset: async (payload: {
    spec_key: string;
    label: string;
    field_type: string;
    respondent_role?: string;
  }): Promise<import('../types/changes').InternalSubtaskPresetField> => {
    const response = await api.post('/api/changes/internal-subtask-presets', payload);
    return response.data;
  },

  getInternalSubtaskListLayout: async (): Promise<
    import('../types/changes').InternalSubtaskListLayoutResponse
  > => {
    const response = await api.get('/api/changes/internal-subtask-list-layout');
    return response.data;
  },

  replaceInternalSubtaskListLayout: async (
    columnKeys: string[],
  ): Promise<import('../types/changes').InternalSubtaskListLayoutResponse> => {
    const response = await api.put('/api/changes/internal-subtask-list-layout', {
      column_keys: columnKeys,
    });
    return response.data;
  },

  listInternalSubtaskTemplates: async (params?: {
    only_active?: boolean;
    catalog_only?: boolean;
  }): Promise<{ items: import('../types/changes').InternalSubtaskTemplate[] }> => {
    const response = await api.get('/api/changes/internal-subtask-templates', { params });
    return response.data;
  },

  createInternalSubtaskTemplate: async (payload: {
    name: string;
    default_title?: string | null;
    task_type_id?: number | null;
    allow_thread_default?: boolean;
    show_on_dispatch?: boolean;
    group_ids?: number[];
    as_personal?: boolean;
  }): Promise<import('../types/changes').InternalSubtaskTemplate> => {
    const response = await api.post('/api/changes/internal-subtask-templates', payload);
    return response.data;
  },

  updateInternalSubtaskTemplate: async (
    templateId: number,
    payload: Record<string, unknown>,
  ): Promise<import('../types/changes').InternalSubtaskTemplate> => {
    const response = await api.put(`/api/changes/internal-subtask-templates/${templateId}`, payload);
    return response.data;
  },

  deleteInternalSubtaskTemplate: async (templateId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/internal-subtask-templates/${templateId}`);
    return response.data;
  },

  listInternalSubtaskTemplateFields: async (
    templateId: number,
  ): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.get(`/api/changes/internal-subtask-templates/${templateId}/fields`);
    return response.data;
  },

  createInternalSubtaskTemplateField: async (
    templateId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.post(
      `/api/changes/internal-subtask-templates/${templateId}/fields`,
      payload,
    );
    return response.data as ChangeFormField;
  },

  updateInternalSubtaskTemplateField: async (
    templateId: number,
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(
      `/api/changes/internal-subtask-templates/${templateId}/fields/${fieldId}`,
      payload,
    );
    return response.data as ChangeFormField;
  },

  deleteInternalSubtaskTemplateField: async (
    templateId: number,
    fieldId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/api/changes/internal-subtask-templates/${templateId}/fields/${fieldId}`,
    );
    return response.data;
  },

  replaceInternalSubtaskTemplateCustomFields: async (
    templateId: number,
    items: Record<string, unknown>[],
  ): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put(
      `/api/changes/internal-subtask-templates/${templateId}/fields/replace-custom`,
      { items },
    );
    return response.data;
  },

  reorderInternalSubtaskTemplateFields: async (
    templateId: number,
    ids: number[],
  ): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put(
      `/api/changes/internal-subtask-templates/${templateId}/fields/reorder`,
      { ids },
    );
    return response.data;
  },

  replaceInternalSubtaskFormTabs: async (
    templateId: number,
    payload: {
      items: string[];
      rename_from?: string | null;
      rename_to?: string | null;
      position?: 'before' | 'after' | null;
    },
  ): Promise<{ items: string[]; position?: 'before' | 'after' }> => {
    const response = await api.put(
      `/api/changes/internal-subtask-templates/${templateId}/form-tabs`,
      payload,
    );
    return response.data;
  },

  replaceInternalSubtaskTemplateGroups: async (
    templateId: number,
    groupIds: number[],
  ): Promise<import('../types/changes').InternalSubtaskTemplate> => {
    const response = await api.put(`/api/changes/internal-subtask-templates/${templateId}/groups`, {
      group_ids: groupIds,
    });
    return response.data;
  },

  importInternalSubtaskFromGroup: async (
    groupId: number,
  ): Promise<import('../types/changes').InternalSubtaskTemplate> => {
    const response = await api.post(`/api/changes/internal-subtask-templates/import-from-group/${groupId}`);
    return response.data;
  },

  getPostImplFormSchema: async (): Promise<{
    items: ChangeFormField[];
    post_impl_editor_roles?: Array<'admin' | 'gestor' | 'dono' | 'solicitante'>;
  }> => {
    const response = await api.get('/api/changes/post-impl-form-schema');
    return response.data;
  },

  createPostImplFormField: async (payload: Record<string, unknown>): Promise<ChangeFormField> => {
    const response = await api.post('/api/changes/post-impl-form-fields', payload);
    return response.data as ChangeFormField;
  },

  updatePostImplFormField: async (
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(`/api/changes/post-impl-form-fields/${fieldId}`, payload);
    return response.data as ChangeFormField;
  },

  deletePostImplFormField: async (fieldId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/post-impl-form-fields/${fieldId}`);
    return response.data;
  },

  reorderPostImplFormFields: async (ids: number[]): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put('/api/changes/post-impl-form-fields/reorder', { ids });
    return response.data;
  },

  updatePostImplEditorRoles: async (payload: {
    post_impl_editor_roles: Array<'admin' | 'gestor' | 'dono' | 'solicitante'>;
  }): Promise<{ post_impl_editor_roles: Array<'admin' | 'gestor' | 'dono' | 'solicitante'> }> => {
    const response = await api.put('/api/changes/post-impl-form/editor-roles', payload);
    return response.data;
  },

  listDetailTabs: async (): Promise<{ items: ChangeDetailTabDefinition[] }> => {
    const response = await api.get('/api/changes/detail-tabs');
    return response.data;
  },

  getDetailTab: async (tabId: number): Promise<ChangeDetailTabDefinition> => {
    const response = await api.get(`/api/changes/detail-tabs/${tabId}`);
    return response.data;
  },

  createDetailTab: async (
    payload: Record<string, unknown>,
  ): Promise<ChangeDetailTabDefinition> => {
    const response = await api.post('/api/changes/detail-tabs', payload);
    return response.data;
  },

  updateDetailTab: async (
    tabId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeDetailTabDefinition> => {
    const response = await api.put(`/api/changes/detail-tabs/${tabId}`, payload);
    return response.data;
  },

  deleteDetailTab: async (tabId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/detail-tabs/${tabId}`);
    return response.data;
  },

  reorderDetailTabs: async (ids: number[]): Promise<{ items: ChangeDetailTabDefinition[] }> => {
    const response = await api.put('/api/changes/detail-tabs/reorder', { ids });
    return response.data;
  },

  getDetailPageTabLayout: async (): Promise<ChangeDetailPageTabLayoutResponse> => {
    const response = await api.get('/api/changes/detail-tabs/page-layout');
    return response.data;
  },

  replaceDetailPageTabLayout: async (order: string[]): Promise<ChangeDetailPageTabLayoutResponse> => {
    const response = await api.put('/api/changes/detail-tabs/page-layout', { order });
    return response.data;
  },

  createDetailTabField: async (
    tabId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeDetailTabField> => {
    const response = await api.post(`/api/changes/detail-tabs/${tabId}/fields`, payload);
    return response.data;
  },

  updateDetailTabField: async (
    tabId: number,
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeDetailTabField> => {
    const response = await api.put(`/api/changes/detail-tabs/${tabId}/fields/${fieldId}`, payload);
    return response.data;
  },

  deleteDetailTabField: async (
    tabId: number,
    fieldId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/detail-tabs/${tabId}/fields/${fieldId}`);
    return response.data;
  },

  reorderDetailTabFields: async (
    tabId: number,
    ids: number[],
  ): Promise<ChangeDetailTabDefinition> => {
    const response = await api.put(`/api/changes/detail-tabs/${tabId}/fields/reorder`, { ids });
    return response.data;
  },

  listVisibleDetailTabs: async (
    changeId: number,
    status?: string,
  ): Promise<{ items: ChangeDetailTabDefinition[]; status: string; page_tab_order?: string[] }> => {
    const response = await api.get(`/api/changes/${changeId}/detail-tabs`, {
      params: status ? { status } : undefined,
    });
    return response.data;
  },

  listDetailTabEntries: async (
    changeId: number,
    tabId: number,
    params: { page?: number; limit?: number; q?: string } = {},
  ): Promise<{
    items: ChangeDetailTabEntrySlim[];
    total: number;
    page: number;
    limit: number;
    tab: ChangeDetailTabDefinition;
    can_create: boolean;
    can_edit: boolean;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/detail-tabs/${tabId}/entries`, {
      params,
    });
    return response.data;
  },

  getDetailTabEntry: async (
    changeId: number,
    tabId: number,
    entryId: number,
  ): Promise<ChangeDetailTabEntryDetail> => {
    const response = await api.get(
      `/api/changes/${changeId}/detail-tabs/${tabId}/entries/${entryId}`,
    );
    return response.data;
  },

  createDetailTabEntry: async (
    changeId: number,
    tabId: number,
    values: Record<string, unknown>,
  ): Promise<ChangeDetailTabEntryDetail> => {
    const response = await api.post(`/api/changes/${changeId}/detail-tabs/${tabId}/entries`, {
      values,
    });
    return response.data;
  },

  updateDetailTabEntry: async (
    changeId: number,
    tabId: number,
    entryId: number,
    values: Record<string, unknown>,
  ): Promise<ChangeDetailTabEntryDetail> => {
    const response = await api.put(
      `/api/changes/${changeId}/detail-tabs/${tabId}/entries/${entryId}`,
      { values },
    );
    return response.data;
  },

  deleteDetailTabEntry: async (
    changeId: number,
    tabId: number,
    entryId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/api/changes/${changeId}/detail-tabs/${tabId}/entries/${entryId}`,
    );
    return response.data;
  },

  deleteCatalogItem: async (
    kind: ChangeCatalogKind,
    itemId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/${kind}/${itemId}`);
    return response.data;
  },

  calendar: async (params: {
    date_from?: string;
    date_to?: string;
    category_id?: number;
    project?: string;
    owner_id?: number;
  } = {}): Promise<{
    items: ChangeCalendarItem[];
    meetings?: ChangeMeetingLite[];
    holidays?: ChangeCalendarHoliday[];
    default_window_weekday?: number;
    default_risk_weekday?: number;
    default_camud_weekday?: number;
  }> => {
    const response = await api.get('/api/changes/calendar', { params });
    return response.data;
  },

  listMeetings: async (params: {
    skip?: number;
    limit?: number;
    date_from?: string;
    date_to?: string;
    kind?: ChangeMeetingKind;
  } = {}): Promise<{ items: ChangeMeetingLite[]; total: number; skip: number; limit: number }> => {
    const response = await api.get('/api/changes/meetings', { params });
    return response.data;
  },

  createMeeting: async (payload: {
    kind: ChangeMeetingKind;
    title?: string;
    scheduled_at: string;
    countdown_seconds?: number;
    change_ids?: number[];
  }): Promise<ChangeMeeting> => {
    const response = await api.post('/api/changes/meetings', payload);
    return response.data;
  },

  getMeeting: async (meetingId: number): Promise<ChangeMeeting> => {
    const response = await api.get(`/api/changes/meetings/${meetingId}`);
    return response.data;
  },

  sendMeetingAgenda: async (
    meetingId: number,
    payload: {
      agenda_kind?: AgendaKind;
      send_at?: string;
      offset_days_before?: number;
      send_time?: string;
      extra_emails?: string[];
      recipient_person_ids?: number[];
      layout?: AgendaLayoutKey;
      use_custom_layout?: boolean;
      custom_subject?: string;
      custom_body_html?: string;
    },
    files?: File[],
  ): Promise<{
    success: boolean;
    sent: number;
    changes: number;
    send_log_id?: number;
    outcome?: string;
    queued?: boolean;
    queued_id?: number;
    send_at?: string;
    meeting_id?: number;
  }> => {
    if (files?.length) {
      const form = new FormData();
      form.append('payload', JSON.stringify(payload));
      files.forEach((file) => form.append('files', file));
      const response = await api.post(`/api/changes/meetings/${meetingId}/agenda/send`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await api.post(`/api/changes/meetings/${meetingId}/agenda/send`, payload);
    return response.data;
  },

  previewMeetingAgendaEmail: async (
    meetingId: number,
    params?: { agenda_kind?: AgendaKind; layout?: AgendaLayoutKey },
  ): Promise<ChangeMeetingAgendaEmailPreview> => {
    const response = await api.get(`/api/changes/meetings/${meetingId}/agenda/email-preview`, {
      params,
    });
    return response.data;
  },

  updateMeeting: async (
    meetingId: number,
    payload: { title?: string; kind?: ChangeMeetingKind; scheduled_at?: string; countdown_seconds?: number },
  ): Promise<ChangeMeeting> => {
    const response = await api.put(`/api/changes/meetings/${meetingId}`, payload);
    return response.data;
  },

  deleteMeeting: async (
    meetingId: number,
  ): Promise<{ success: boolean; id: number; detached_change_ids: number[] }> => {
    const response = await api.delete(`/api/changes/meetings/${meetingId}`);
    return response.data;
  },

  addMeetingItem: async (meetingId: number, changeId: number): Promise<ChangeMeeting> => {
    return changesApi.addMeetingItems(meetingId, [changeId]);
  },

  addMeetingItems: async (meetingId: number, changeIds: number[]): Promise<ChangeMeeting> => {
    const response = await api.post(`/api/changes/meetings/${meetingId}/items`, { change_ids: changeIds });
    return response.data;
  },

  reorderMeetingItems: async (meetingId: number, ids: number[]): Promise<ChangeMeeting> => {
    const response = await api.put(`/api/changes/meetings/${meetingId}/items/reorder`, { ids });
    return response.data;
  },

  removeMeetingItem: async (meetingId: number, itemId: number): Promise<ChangeMeeting> => {
    const response = await api.delete(`/api/changes/meetings/${meetingId}/items/${itemId}`);
    return response.data;
  },

  startMeetingItem: async (meetingId: number, itemId: number): Promise<ChangeMeeting> => {
    const response = await api.post(`/api/changes/meetings/${meetingId}/items/${itemId}/start`);
    return response.data;
  },

  stopMeetingItem: async (meetingId: number, itemId: number): Promise<ChangeMeeting> => {
    const response = await api.post(`/api/changes/meetings/${meetingId}/items/${itemId}/stop`);
    return response.data;
  },

  nextMeetingItem: async (meetingId: number, fromItemId?: number): Promise<ChangeMeeting> => {
    const response = await api.post(
      `/api/changes/meetings/${meetingId}/next`,
      undefined,
      { params: fromItemId ? { from_item_id: fromItemId } : undefined },
    );
    return response.data;
  },

  closeMeeting: async (meetingId: number): Promise<ChangeMeeting> => {
    const response = await api.post(`/api/changes/meetings/${meetingId}/close`);
    return response.data;
  },

  listCabSlides: async (): Promise<{
    items: ChangeCabSlide[];
    fields: ChangeCabSlideField[];
    default_countdown_seconds: number;
  }> => {
    const response = await api.get('/api/changes/cab-slides');
    return response.data;
  },

  createCabSlide: async (payload: {
    title: string;
    field_keys: string[];
    is_active?: boolean;
  }): Promise<ChangeCabSlide> => {
    const response = await api.post('/api/changes/cab-slides', payload);
    return response.data;
  },

  updateCabSlide: async (
    slideId: number,
    payload: { title?: string; field_keys?: string[]; is_active?: boolean },
  ): Promise<ChangeCabSlide> => {
    const response = await api.put(`/api/changes/cab-slides/${slideId}`, payload);
    return response.data;
  },

  deleteCabSlide: async (slideId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/cab-slides/${slideId}`);
    return response.data;
  },

  reorderCabSlides: async (ids: number[]): Promise<{ items: ChangeCabSlide[] }> => {
    const response = await api.put('/api/changes/cab-slides/reorder', { ids });
    return response.data;
  },

  getGlobalCabDeck: async (): Promise<ChangeCabDeck> => {
    const response = await api.get('/api/changes/cab-decks/global');
    return response.data;
  },

  updateGlobalCabDeckTheme: async (theme: Partial<ChangeCabDeckTheme>): Promise<ChangeCabDeck> => {
    const response = await api.put('/api/changes/cab-decks/global/theme', { theme });
    return response.data;
  },

  getChangeCabDeck: async (changeId: number): Promise<ChangeCabDeck> => {
    const response = await api.get(`/api/changes/${changeId}/cab-deck`);
    return response.data;
  },

  saveCabDeck: async (
    deckId: number,
    payload: { theme?: Partial<ChangeCabDeckTheme>; slides?: ChangeCabDeck['slides'] },
  ): Promise<ChangeCabDeck> => {
    const response = await api.put(`/api/changes/cab-decks/${deckId}`, payload);
    return response.data;
  },

  deleteChangeCabDeck: async (changeId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/${changeId}/cab-deck`);
    return response.data;
  },

  appendCabDeckFromGlobal: async (changeId: number): Promise<ChangeCabDeck> => {
    const response = await api.post(`/api/changes/${changeId}/cab-deck/append-from-global`);
    return response.data;
  },

  uploadCabDeckImage: async (file: File, changeId?: number): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/api/changes/cab-decks/upload-image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: changeId != null && Number.isFinite(changeId) ? { change_id: changeId } : undefined,
    });
    return response.data;
  },

  agenda: async (params?: {
    window_start?: string;
    window_end?: string;
    category_id?: number;
    project?: string;
    owner_id?: number;
  }): Promise<ChangeAgendaPreview & { window_start: string; window_end: string }> => {
    const response = await api.get('/api/changes/agenda', { params });
    return response.data;
  },

  sendAgenda: async (payload: {
    agenda_kind: AgendaKind;
    window_start?: string;
    window_end?: string;
    category_id?: number;
    project?: string;
    owner_id?: number;
    change_ids?: number[];
    extra_emails?: string[];
    recipient_person_ids?: number[];
    use_custom_layout?: boolean;
    custom_subject?: string;
    custom_body_html?: string;
    layout?: AgendaLayoutKey;
    send_at?: string;
  }): Promise<{
    success: boolean;
    sent: number;
    changes: number;
    send_log_id?: number;
    outcome?: string;
    queued?: boolean;
    queued_id?: number;
    send_at?: string;
  }> => {
    const response = await api.post('/api/changes/agenda/send', payload);
    return response.data;
  },

  listAgendaSends: async (params?: {
    skip?: number;
    limit?: number;
    agenda_kind?: AgendaKind;
    date_from?: string;
    date_to?: string;
  }): Promise<{ total: number; skip?: number; limit?: number; items: ChangeAgendaSend[] }> => {
    const response = await api.get('/api/changes/agenda/sends', { params });
    return response.data;
  },

  listAgendaRecipients: async (params: {
    change_id?: number;
    skip?: number;
    limit?: number;
    search?: string;
    agenda_kind?: AgendaKind;
  }): Promise<{
    items: ChangeAgendaRecipient[];
    total: number;
    skip: number;
    limit: number;
    committees?: ChangeAgendaRecipientCommittee[];
  }> => {
    const response = await api.get('/api/changes/agenda/recipients', { params });
    return response.data;
  },

  listCommittees: async (params: {
    skip?: number;
    limit?: number;
    search?: string;
    only_active?: boolean;
  } = {}): Promise<{ items: ChangeCommittee[]; total: number; skip: number; limit: number }> => {
    const response = await api.get('/api/changes/committees', { params });
    return response.data;
  },

  createCommittee: async (payload: { name: string }): Promise<ChangeCommittee> => {
    const response = await api.post('/api/changes/committees', payload);
    return response.data;
  },

  updateCommittee: async (
    committeeId: number,
    payload: { name?: string; is_active?: boolean },
  ): Promise<ChangeCommittee> => {
    const response = await api.put(`/api/changes/committees/${committeeId}`, payload);
    return response.data;
  },

  deleteCommittee: async (committeeId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/committees/${committeeId}`);
    return response.data;
  },

  listCommitteePeople: async (
    committeeId: number,
    params: { skip?: number; limit?: number; search?: string } = {},
  ): Promise<{ items: ChangeCommitteePerson[]; total: number; skip: number; limit: number }> => {
    const response = await api.get(`/api/changes/committees/${committeeId}/people`, { params });
    return response.data;
  },

  addCommitteePerson: async (
    committeeId: number,
    payload: { name: string; email: string; receives_email?: boolean },
  ): Promise<ChangeCommitteePerson> => {
    const response = await api.post(`/api/changes/committees/${committeeId}/people`, payload);
    return response.data;
  },

  updateCommitteePerson: async (
    committeeId: number,
    personId: number,
    payload: { name?: string; email?: string; receives_email?: boolean },
  ): Promise<ChangeCommitteePerson> => {
    const response = await api.put(
      `/api/changes/committees/${committeeId}/people/${personId}`,
      payload,
    );
    return response.data;
  },

  deleteCommitteePerson: async (
    committeeId: number,
    personId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/committees/${committeeId}/people/${personId}`);
    return response.data;
  },

  listAgendaEmailVariables: async (): Promise<{ items: ChangeAgendaEmailVariable[] }> => {
    const response = await api.get('/api/changes/agenda/email-template/variables');
    return response.data;
  },

  getGlobalAgendaEmailTemplate: async (): Promise<ChangeAgendaEmailTemplate> => {
    const response = await api.get('/api/changes/agenda/email-template');
    return response.data;
  },

  saveGlobalAgendaEmailTemplate: async (payload: {
    enabled: boolean;
    subject: string;
    body_html: string;
  }): Promise<ChangeAgendaEmailTemplate> => {
    const response = await api.put('/api/changes/agenda/email-template', payload);
    return response.data;
  },

  getCommitteeEmailTemplate: async (committeeId: number): Promise<ChangeAgendaEmailTemplate> => {
    const response = await api.get(`/api/changes/committees/${committeeId}/email-template`);
    return response.data;
  },

  saveCommitteeEmailTemplate: async (
    committeeId: number,
    payload: { enabled: boolean; subject: string; body_html: string },
  ): Promise<ChangeAgendaEmailTemplate> => {
    const response = await api.put(`/api/changes/committees/${committeeId}/email-template`, payload);
    return response.data;
  },

  listAgendaEmailLayouts: async (): Promise<{ items: ChangeAgendaEmailTemplate[] }> => {
    const response = await api.get('/api/changes/agenda/email-layouts');
    return response.data;
  },

  listAgendaEmailSchedules: async (): Promise<{ items: ChangeAgendaEmailSchedule[] }> => {
    const response = await api.get('/api/changes/agenda/email-schedules');
    return response.data;
  },

  saveAgendaEmailSchedules: async (payload: {
    items: Array<{
      agenda_kind: 'committee' | 'camud';
      enabled: boolean;
      offset_days: number;
      send_time: string;
    }>;
  }): Promise<{ items: ChangeAgendaEmailSchedule[] }> => {
    const response = await api.put('/api/changes/agenda/email-schedules', payload);
    return response.data;
  },

  getAgendaSend: async (sendId: number): Promise<ChangeAgendaSend> => {
    const response = await api.get(`/api/changes/agenda/sends/${sendId}`);
    return response.data;
  },

  agendaSendSummary: async (changeId: number): Promise<ChangeAgendaSendSummary> => {
    const response = await api.get(`/api/changes/${changeId}/agenda-sends/summary`);
    return response.data;
  },

  meetingAgendaSendSummary: async (meetingId: number): Promise<ChangeMeetingAgendaSendSummary> => {
    const response = await api.get(`/api/changes/meetings/${meetingId}/agenda-sends/summary`);
    return response.data;
  },

  cancelQueuedAgendaSend: async (
    queuedId: number,
  ): Promise<{ success: boolean; queued_id: number; status: string }> => {
    const response = await api.post(`/api/changes/agenda/queued/${queuedId}/cancel`);
    return response.data;
  },

  listEmailTriggers: async (): Promise<{ items: ChangeEmailTriggerConfig[] }> => {
    const response = await api.get('/api/changes/email-triggers');
    return response.data;
  },

  getEmailTrigger: async (triggerKey: string): Promise<ChangeEmailTriggerConfig> => {
    const response = await api.get(`/api/changes/email-triggers/${triggerKey}`);
    return response.data;
  },

  saveEmailTrigger: async (
    triggerKey: string,
    payload: Omit<
      ChangeEmailTriggerConfig,
      'trigger_key' | 'label' | 'configured' | 'updated_at'
    >,
  ): Promise<ChangeEmailTriggerConfig> => {
    const response = await api.put(`/api/changes/email-triggers/${triggerKey}`, payload);
    return response.data;
  },

  listEmailTriggerVariables: async (
    triggerKey: string,
  ): Promise<{ items: ChangeAgendaEmailVariable[] }> => {
    const response = await api.get(`/api/changes/email-triggers/${triggerKey}/variables`);
    return response.data;
  },

  listEmailTriggerQueued: async (
    triggerKey: string,
  ): Promise<{ items: ChangeEmailTriggerQueuedItem[] }> => {
    const response = await api.get(`/api/changes/email-triggers/${triggerKey}/queued`);
    return response.data;
  },

  dispatchEmailTrigger: async (
    triggerKey: string,
    payload: { change_id: number; subtask_id?: number; send_at?: string },
  ): Promise<{ success: boolean; sent?: number; queued_id?: number; send_at?: string; outcome?: string }> => {
    const response = await api.post(`/api/changes/email-triggers/${triggerKey}/dispatch`, payload);
    return response.data;
  },

  cancelEmailTriggerQueued: async (
    queuedId: number,
  ): Promise<{ success: boolean; queued_id: number; status: string }> => {
    const response = await api.post(`/api/changes/email-triggers/queued/${queuedId}/cancel`);
    return response.data;
  },

  listEmailQueue: async (): Promise<{ items: ChangeEmailQueueItem[]; total: number }> => {
    const response = await api.get('/api/changes/email-queue');
    return response.data;
  },

  dispatchEmailQueueNow: async (
    kind: 'agenda' | 'trigger',
    queuedId: number,
  ): Promise<{ success?: boolean; queued_id: number; status?: string; outcome?: string }> => {
    const response = await api.post(`/api/changes/email-queue/${kind}/${queuedId}/dispatch-now`);
    return response.data;
  },

  dashboardIndicators: async (params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<Record<string, unknown>> => {
    const response = await api.get('/api/changes/dashboard/indicators', { params });
    return response.data;
  },

  listWorkGroups: async (
    params: { only_active?: boolean } = {},
  ): Promise<{ items: ChangeWorkGroup[] }> => {
    const response = await api.get('/api/changes/work-groups', { params });
    return response.data;
  },

  exportWorkGroups: async (): Promise<ChangeWorkGroupsExportPayload> => {
    const response = await api.get('/api/changes/work-groups/export');
    return response.data as ChangeWorkGroupsExportPayload;
  },

  importWorkGroups: async (
    payload: { version?: string; module?: string; groups: ChangeWorkGroupExportItem[] },
    dryRun: boolean,
  ): Promise<ChangeWorkGroupsImportResponse> => {
    const response = await api.post('/api/changes/work-groups/import', payload, {
      params: { dry_run: dryRun },
    });
    return response.data as ChangeWorkGroupsImportResponse;
  },

  linkWorkGroup: async (groupId: number): Promise<ChangeWorkGroup> => {
    const response = await api.post('/api/changes/work-groups', { group_id: groupId });
    return response.data as ChangeWorkGroup;
  },

  createWorkGroup: async (payload: {
    name: string;
    description?: string | null;
  }): Promise<ChangeWorkGroup> => {
    const response = await api.post('/api/changes/work-groups', payload);
    return response.data as ChangeWorkGroup;
  },

  getApprovalSettings: async (): Promise<ChangeApprovalSettings> => {
    const response = await api.get('/api/changes/approval-settings');
    return response.data as ChangeApprovalSettings;
  },

  replaceApprovalSettings: async (payload: {
    enable_riscos_impactos: boolean;
    enable_camud_go_no_go: boolean;
    enable_gestao_mudancas?: boolean;
    creator_approver_roles?: Array<'criador' | 'gestor'>;
    plan_editor_roles?: Array<'admin' | 'gestor' | 'criador' | 'dono' | 'solicitante'>;
    approval_order_mode?: 'livre' | 'criador_primeiro';
    form_editable_statuses?: string[];
  }): Promise<ChangeApprovalSettings> => {
    const response = await api.put('/api/changes/approval-settings', payload);
    return response.data as ChangeApprovalSettings;
  },

  getApprovalFormSchema: async (): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.get('/api/changes/approval-form-schema');
    return response.data;
  },

  createApprovalFormField: async (payload: Record<string, unknown>): Promise<ChangeFormField> => {
    const response = await api.post('/api/changes/approval-form-fields', payload);
    return response.data as ChangeFormField;
  },

  updateApprovalFormField: async (
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(`/api/changes/approval-form-fields/${fieldId}`, payload);
    return response.data as ChangeFormField;
  },

  deleteApprovalFormField: async (fieldId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/changes/approval-form-fields/${fieldId}`);
    return response.data;
  },

  reorderApprovalFormFields: async (ids: number[]): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put('/api/changes/approval-form-fields/reorder', { ids });
    return response.data;
  },

  listTypePolicies: async (params: { only_active?: boolean } = {}): Promise<{ items: ChangeTypePolicy[] }> => {
    const response = await api.get('/api/changes/type-policies', { params });
    return response.data as { items: ChangeTypePolicy[] };
  },

  createTypePolicy: async (payload: {
    code: string;
    name: string;
    description?: string | null;
    enable_internal_subtasks?: boolean;
    enable_external_subtasks?: boolean;
    is_active?: boolean;
  }): Promise<ChangeTypePolicy> => {
    const response = await api.post('/api/changes/type-policies', payload);
    return response.data as ChangeTypePolicy;
  },

  updateTypePolicy: async (
    policyId: number,
    payload: {
      name?: string;
      description?: string | null;
      enable_internal_subtasks?: boolean;
      enable_external_subtasks?: boolean;
      is_active?: boolean;
    },
  ): Promise<ChangeTypePolicy> => {
    const response = await api.put(`/api/changes/type-policies/${policyId}`, payload);
    return response.data as ChangeTypePolicy;
  },

  getEmergencyPolicy: async (): Promise<ChangeEmergencyPolicy> => {
    const response = await api.get('/api/changes/emergency-policy');
    return response.data as ChangeEmergencyPolicy;
  },

  replaceEmergencyPolicy: async (payload: ChangeEmergencyPolicy): Promise<ChangeEmergencyPolicy> => {
    const response = await api.put('/api/changes/emergency-policy', payload);
    return response.data as ChangeEmergencyPolicy;
  },

  listTypeApprovalFlows: async (): Promise<{ items: ChangeTypeApprovalFlowSummary[] }> => {
    const response = await api.get('/api/changes/type-approval-flows');
    return response.data as { items: ChangeTypeApprovalFlowSummary[] };
  },

  getTypeApprovalFlow: async (changeTypeCode: string): Promise<ChangeTypeApprovalFlow> => {
    const response = await api.get(
      `/api/changes/type-approval-flows/${encodeURIComponent(changeTypeCode)}`,
    );
    return response.data as ChangeTypeApprovalFlow;
  },

  replaceTypeApprovalFlow: async (
    changeTypeCode: string,
    payload: Pick<
      ChangeTypeApprovalFlow,
      'flow_enabled' | 'use_immediate_manager' | 'parallel_after_first' | 'steps'
    >,
  ): Promise<ChangeTypeApprovalFlow> => {
    const response = await api.put(
      `/api/changes/type-approval-flows/${encodeURIComponent(changeTypeCode)}`,
      payload,
    );
    return response.data as ChangeTypeApprovalFlow;
  },

  listApprovalFlowConfigs: async (): Promise<{ items: ChangeApprovalFlowConfig[] }> => {
    const response = await api.get('/api/changes/approval-flow-configs');
    return response.data as { items: ChangeApprovalFlowConfig[] };
  },

  getApprovalFlowConfig: async (configId: number): Promise<ChangeApprovalFlowConfig> => {
    const response = await api.get(`/api/changes/approval-flow-configs/${configId}`);
    return response.data as ChangeApprovalFlowConfig;
  },

  createApprovalFlowConfig: async (
    payload: Omit<ChangeApprovalFlowConfig, 'id' | 'change_type_name'>,
  ): Promise<ChangeApprovalFlowConfig> => {
    const response = await api.post('/api/changes/approval-flow-configs', payload);
    return response.data as ChangeApprovalFlowConfig;
  },

  replaceApprovalFlowConfig: async (
    configId: number,
    payload: Omit<ChangeApprovalFlowConfig, 'id' | 'change_type_name'>,
  ): Promise<ChangeApprovalFlowConfig> => {
    const response = await api.put(`/api/changes/approval-flow-configs/${configId}`, payload);
    return response.data as ChangeApprovalFlowConfig;
  },

  setApprovalFlowConfigActive: async (
    configId: number,
    isActive: boolean,
  ): Promise<ChangeApprovalFlowConfig> => {
    const response = await api.patch(`/api/changes/approval-flow-configs/${configId}/active`, {
      is_active: isActive,
    });
    return response.data as ChangeApprovalFlowConfig;
  },

  deleteApprovalFlowConfig: async (configId: number): Promise<{ ok: boolean }> => {
    const response = await api.delete(`/api/changes/approval-flow-configs/${configId}`);
    return response.data as { ok: boolean };
  },

  getSapRequestTransportSettings: async (): Promise<ChangeSapRequestTransportSettings> => {
    const response = await api.get('/api/changes/sap-request-transport-settings');
    return response.data as ChangeSapRequestTransportSettings;
  },

  replaceSapRequestTransportSettings: async (
    payload: Pick<ChangeSapRequestTransportSettings, 'is_enabled' | 'group_id' | 'assignee_id'>,
  ): Promise<ChangeSapRequestTransportSettings> => {
    const response = await api.put('/api/changes/sap-request-transport-settings', payload);
    return response.data as ChangeSapRequestTransportSettings;
  },

  replaceApprovalPlan: async (
    changeId: number,
    tracks: Array<{
      track: string;
      group_ids: number[];
      people?: Array<{ group_id: number; assignee_id: number }>;
    }>,
  ): Promise<ChangeRequest> => {
    const response = await api.put(`/api/changes/${changeId}/approval-plan`, { tracks });
    return response.data as ChangeRequest;
  },

  voidApprovals: async (changeId: number): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/approvals/void`);
    return response.data as ChangeRequest;
  },

  voidApproval: async (changeId: number, approvalId: number): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/approvals/${approvalId}/void`);
    return response.data as ChangeRequest;
  },

  resetToScheduled: async (changeId: number): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/reset-to-scheduled`);
    return response.data as ChangeRequest;
  },

  respawnTransportApprovals: async (
    changeId: number,
    payload: { all_areas?: boolean; approval_id?: number; force_new_round?: boolean },
  ): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/transport-approvals/respawn`, payload);
    return response.data as ChangeRequest;
  },

  setWorkGroupActive: async (groupId: number, isActive: boolean): Promise<ChangeWorkGroup> => {
    const response = await api.put(`/api/changes/work-groups/${groupId}`, { is_active: isActive });
    return response.data as ChangeWorkGroup;
  },

  updateWorkGroup: async (
    groupId: number,
    payload: {
      name?: string;
      description?: string | null;
      is_active?: boolean;
      auto_subtask_on_normal?: boolean;
      auto_subtask_allow_thread?: boolean;
      internal_subtask_template_id?: number | null;
      internal_subtask_template_ids?: number[];
      open_in_incidents?: boolean;
      incidents_group_id?: number | null;
      incidents_workflow_id?: number | null;
      prompt_on_create?: boolean;
      incident_attachment_allowed?: boolean;
      incident_title_default?: string | null;
      incident_description_default?: string | null;
      incident_urgency_default?: string | null;
      incident_custom_field_defaults?: Record<string, unknown> | null;
      incident_kind?: 'si' | 'infra_nuvem' | 'infra_hosting' | null;
    },
  ): Promise<ChangeWorkGroup> => {
    const response = await api.put(`/api/changes/work-groups/${groupId}`, payload);
    return response.data as ChangeWorkGroup;
  },

  listIncidentTargets: async (): Promise<{
    groups: Array<{ id: number; name: string }>;
    workflows: Array<{ id: number; name: string; group_ids: number[] }>;
  }> => {
    type TargetWorkflow = { id: number; name: string; group_ids: number[] };
    type PublishedRow = {
      id: number;
      name: string;
      groups?: Array<{ id: number }>;
    };
    const publishedIsActive = (row: PublishedRow): boolean =>
      (row as PublishedRow & { is_active?: boolean }).is_active !== false;
    const blockedArea = (row: PublishedRow): boolean => {
      const area = String((row as PublishedRow & { area_type?: string }).area_type || 'tickets').toLowerCase();
      return area === 'purchases' || area === 'projects';
    };
    const toWorkflows = (rows: PublishedRow[], linked: Map<number, number[]>): TargetWorkflow[] =>
      rows
        .filter(
          (row: PublishedRow) =>
            Boolean(row && row.id && String(row.name || '').trim()) &&
            publishedIsActive(row) &&
            !blockedArea(row),
        )
        .sort((a: PublishedRow, b: PublishedRow) => String(a.name).localeCompare(String(b.name), 'pt-BR'))
        .map((row: PublishedRow) => ({
          id: Number(row.id),
          name: String(row.name),
          group_ids: (row.groups || []).map((group: { id: number }) => Number(group.id)).filter((id: number) => id > 0)
            .length
            ? (row.groups || []).map((group: { id: number }) => Number(group.id))
            : linked.get(Number(row.id)) || [],
        }));

    const [publishedRes, targetsRes, allTicketsRes] = await Promise.all([
      api
        .get('/api/workflows/published', { params: { area_type: 'tickets', limit: 500 } })
        .catch(() =>
          axios.get(`${API_URL}/api/workflows/published`, {
            params: { area_type: 'tickets', limit: 500 },
          }),
        )
        .catch(() => ({ data: [] as PublishedRow[] })),
      api.get('/api/changes/work-groups/incident-targets').catch(() => ({
        data: { groups: [] as Array<{ id: number; name: string }>, workflows: [] as TargetWorkflow[] },
      })),
      api
        .get('/api/workflows', { params: { area_type: 'tickets', only_valid: false, limit: 1000 } })
        .catch(() => ({ data: [] as PublishedRow[] })),
    ]);
    const publishedRows: PublishedRow[] = Array.isArray(publishedRes.data) ? publishedRes.data : [];
    const allTicketRows: PublishedRow[] = Array.isArray(allTicketsRes.data) ? allTicketsRes.data : [];
    const targets = targetsRes.data || { groups: [], workflows: [] };
    const linked = new Map<number, number[]>(
      ((targets.workflows || []) as TargetWorkflow[]).map((flow: TargetWorkflow) => [
        flow.id,
        flow.group_ids || [],
      ]),
    );
    const byName = new Map<string, TargetWorkflow>();
    [
      ...toWorkflows(publishedRows, linked),
      ...toWorkflows(allTicketRows, linked),
      ...((targets.workflows || []) as TargetWorkflow[]).filter(
        (item) => item.id && String(item.name || '').trim() && !/compra|contrata/i.test(item.name),
      ),
    ].forEach((item) => {
      const key = item.name.trim().toLowerCase();
      if (!byName.has(key)) byName.set(key, item);
    });
    return {
      groups: targets.groups || [],
      workflows: Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    };
  },

  listIncidentWorkflowFields: async (
    workflowId: number,
    groupId?: number,
  ): Promise<import('../types').WorkflowCustomField[]> => {
    const response = await api.get(`/api/workflows/${workflowId}/custom-fields/first-step`, {
      params: groupId ? { group_id: groupId } : undefined,
    });
    return response.data || [];
  },

  listIncidentFirstContactGroups: async (
    workflowId: number,
  ): Promise<Array<{ id: number; name: string }>> => {
    const response = await api.get(`/api/workflows/${workflowId}/first-contact-groups`);
    return (response.data || [])
      .filter((item: { id?: number; name?: string; is_active?: boolean }) => item.id && item.name)
      .map((item: { id: number; name: string }) => ({ id: item.id, name: item.name }));
  },

  uploadIncidentTicketCustomFieldFile: async (
    ticketId: number,
    fieldId: number,
    formData: FormData,
    multiple = false,
  ): Promise<void> => {
    const suffix = multiple ? 'upload-multiple' : 'upload';
    await api.post(`/api/tickets/${ticketId}/custom-fields/${fieldId}/${suffix}`, formData);
  },

  listGroupChecklistFields: async (groupId: number): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.get(`/api/changes/work-groups/${groupId}/checklist-fields`);
    return response.data;
  },

  createGroupChecklistField: async (
    groupId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.post(`/api/changes/work-groups/${groupId}/checklist-fields`, payload);
    return response.data as ChangeFormField;
  },

  updateGroupChecklistField: async (
    groupId: number,
    fieldId: number,
    payload: Record<string, unknown>,
  ): Promise<ChangeFormField> => {
    const response = await api.put(
      `/api/changes/work-groups/${groupId}/checklist-fields/${fieldId}`,
      payload,
    );
    return response.data as ChangeFormField;
  },

  deleteGroupChecklistField: async (
    groupId: number,
    fieldId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/api/changes/work-groups/${groupId}/checklist-fields/${fieldId}`,
    );
    return response.data;
  },

  reorderGroupChecklistFields: async (
    groupId: number,
    ids: number[],
  ): Promise<{ items: ChangeFormField[] }> => {
    const response = await api.put(`/api/changes/work-groups/${groupId}/checklist-fields/reorder`, {
      ids,
    });
    return response.data;
  },

  saveSubtaskChecklist: async (
    changeId: number,
    subtaskId: number,
    payload: { answers: Record<string, ChangeExtraValue> },
  ): Promise<ChangeRequest> => {
    const response = await api.put(`/api/changes/${changeId}/subtasks/${subtaskId}/checklist`, payload);
    return response.data as ChangeRequest;
  },

  addWorkGroupMember: async (
    groupId: number,
    userId: number,
    options?: { is_manager?: boolean },
  ): Promise<ChangeWorkGroup> => {
    const response = await api.post(`/api/changes/work-groups/${groupId}/members`, {
      user_id: userId,
      is_manager: Boolean(options?.is_manager),
    });
    return response.data as ChangeWorkGroup;
  },

  setWorkGroupMemberManager: async (
    groupId: number,
    userId: number,
    isManager: boolean,
  ): Promise<ChangeWorkGroup> => {
    const response = await api.put(`/api/changes/work-groups/${groupId}/members/${userId}`, {
      is_manager: Boolean(isManager),
    });
    return response.data as ChangeWorkGroup;
  },

  setWorkGroupMemberImmediateManager: async (
    groupId: number,
    userId: number,
    immediateManagerUserId: number | null,
  ): Promise<ChangeWorkGroup> => {
    const response = await api.put(
      `/api/changes/work-groups/${groupId}/members/${userId}/immediate-manager`,
      { immediate_manager_user_id: immediateManagerUserId },
    );
    return response.data as ChangeWorkGroup;
  },

  getRequesterManagerOptions: async (requesterId: number, changeType: string) => {
    const response = await api.get('/api/changes/requester-manager-options', {
      params: { requester_id: requesterId, change_type: changeType },
    });
    return response.data as {
      uses_immediate_manager: boolean;
      needs_group_pick: boolean;
      suggested_group_id?: number | null;
      groups: Array<{
        group_id: number;
        group_name?: string | null;
        immediate_manager_user_id?: number | null;
        managers: Array<{ user_id: number; user_name?: string | null; user_email?: string | null }>;
      }>;
      picker_options?: Array<{
        group_id: number;
        group_name?: string | null;
        manager_user_id: number;
        manager_name?: string | null;
        manager_email?: string | null;
      }>;
    };
  },

  addWorkGroupPerson: async (
    groupId: number,
    payload: { name: string; email: string },
  ): Promise<ChangeWorkGroup> => {
    const response = await api.post(`/api/changes/work-groups/${groupId}/people`, payload);
    return response.data as ChangeWorkGroup;
  },

  updateWorkGroupContact: async (
    groupId: number,
    contactId: number,
    payload: { name: string; email: string },
  ): Promise<ChangeWorkGroup> => {
    const response = await api.put(
      `/api/changes/work-groups/${groupId}/contacts/${contactId}`,
      payload,
    );
    return response.data as ChangeWorkGroup;
  },

  deleteWorkGroupContact: async (groupId: number, contactId: number): Promise<ChangeWorkGroup> => {
    const response = await api.delete(`/api/changes/work-groups/${groupId}/contacts/${contactId}`);
    return response.data as ChangeWorkGroup;
  },

  removeWorkGroupMember: async (groupId: number, userId: number): Promise<ChangeWorkGroup> => {
    const response = await api.delete(`/api/changes/work-groups/${groupId}/members/${userId}`);
    return response.data as ChangeWorkGroup;
  },

  assignRequesterImmediateManager: async (
    changeId: number,
    payload: { group_id: number; immediate_manager_user_id: number },
  ) => {
    const response = await api.put(`/api/changes/${changeId}/requester-immediate-manager`, payload);
    return response.data as { setup: RequesterImmediateManagerSetup; change: ChangeRequest };
  },

  approve: async (
    changeId: number,
    approvalId: number,
    justification: string,
    extraFields?: Record<string, unknown>,
  ): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/approve`, {
      approval_id: approvalId,
      justification,
      extra_fields: extraFields || {},
    });
    return response.data as ChangeRequest;
  },

  reject: async (
    changeId: number,
    approvalId: number,
    justification: string,
    extraFields?: Record<string, unknown>,
  ): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/reject`, {
      approval_id: approvalId,
      justification,
      extra_fields: extraFields || {},
    });
    return response.data as ChangeRequest;
  },

  postImplementation: async (
    changeId: number,
    payload: {
      rollback_executed: boolean;
      had_incidents: boolean;
      change_executed: boolean;
      change_executed_successfully: boolean;
      observation?: string | null;
      extra_fields?: Record<string, unknown>;
    },
  ): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/post-implementation`, payload);
    return response.data as ChangeRequest;
  },

  listAttachments: async (changeId: number): Promise<{
    items: Array<{
      id: number;
      filename: string;
      file_path: string;
      file_size?: number | null;
      mime_type?: string | null;
      description?: string | null;
      uploaded_at: string;
    }>;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/attachments`);
    return response.data;
  },

  uploadAttachment: async (changeId: number, formData: FormData): Promise<unknown> => {
    const response = await api.post(`/api/changes/${changeId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  listComments: async (changeId: number): Promise<{
    items: Array<{
      id: number;
      content: string;
      created_at: string;
      is_internal: boolean;
      user?: { id: number; name: string } | null;
    }>;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/comments`);
    return response.data;
  },

  addComment: async (
    changeId: number,
    payload: { content: string; is_internal?: boolean },
  ): Promise<unknown> => {
    const response = await api.post(`/api/changes/${changeId}/comments`, payload);
    return response.data;
  },

  listActivities: async (changeId: number): Promise<{
    items: Array<{
      id: number;
      activity_type: string;
      description: string;
      old_value?: string | null;
      new_value?: string | null;
      created_at: string;
      user?: { id: number; name: string } | null;
    }>;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/activities`);
    return response.data;
  },

  listSecurityAudit: async (params?: {
    skip?: number;
    limit?: number;
    date_from?: string;
    date_to?: string;
    user_id?: number;
    email?: string;
    event_type?: string;
    change_request_id?: number;
  }): Promise<{
    items: Array<Record<string, unknown>>;
    total: number;
    skip: number;
    limit: number;
  }> => {
    const response = await api.get('/api/changes/security-audit', { params });
    return response.data;
  },

  createSubtask: async (
    changeId: number,
    payload: Record<string, unknown>,
  ): Promise<{ success: boolean; id: number; ticket_id?: number | null }> => {
    const response = await api.post(`/api/changes/${changeId}/subtasks`, payload);
    return response.data;
  },

  createSubtaskPackage: async (
    changeId: number,
    payload: Record<string, unknown>,
  ): Promise<{ success: boolean; internal_subtask_ids: number[]; external_count: number }> => {
    const response = await api.post(`/api/changes/${changeId}/subtasks/package`, payload);
    return response.data;
  },

  updateSubtask: async (
    changeId: number,
    subtaskId: number,
    payload: Record<string, unknown>,
  ): Promise<unknown> => {
    const response = await api.put(`/api/changes/${changeId}/subtasks/${subtaskId}`, payload);
    return response.data;
  },

  deleteSubtask: async (changeId: number, subtaskId: number): Promise<ChangeRequest> => {
    const response = await api.delete(`/api/changes/${changeId}/subtasks/${subtaskId}`);
    return response.data as ChangeRequest;
  },

  assumeSubtask: async (changeId: number, subtaskId: number): Promise<ChangeRequest> => {
    const response = await api.post(`/api/changes/${changeId}/subtasks/${subtaskId}/assume`);
    return response.data as ChangeRequest;
  },

  concludeSubtask: async (
    changeId: number,
    subtaskId: number,
  ): Promise<{ success: boolean; id: number; concluded?: boolean }> => {
    const response = await api.post(`/api/changes/${changeId}/subtasks/${subtaskId}/conclude`);
    return response.data;
  },

  listSubtaskReplies: async (
    changeId: number,
    subtaskId: number,
  ): Promise<{ items: Array<{ id: number; user_id: number; user_name?: string | null; content: string; created_at: string }> }> => {
    const response = await api.get(`/api/changes/${changeId}/subtasks/${subtaskId}/replies`);
    return response.data;
  },

  listChangeSubtaskReplies: async (
    changeId: number,
  ): Promise<{
    items: Array<{
      subtask_id: number;
      id: number;
      user_id: number;
      user_name?: string | null;
      content: string;
      created_at: string;
    }>;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/subtask-replies`);
    return response.data;
  },

  addSubtaskReply: async (
    changeId: number,
    subtaskId: number,
    content: string,
  ): Promise<{ items: Array<{ id: number; user_id: number; user_name?: string | null; content: string; created_at: string }> }> => {
    const response = await api.post(`/api/changes/${changeId}/subtasks/${subtaskId}/replies`, { content });
    return response.data;
  },

  uploadSubtaskAttachment: async (
    changeId: number,
    subtaskId: number,
    formData: FormData,
    commentId?: number,
  ): Promise<unknown> => {
    const response = await api.post(
      `/api/changes/${changeId}/subtasks/${subtaskId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: commentId ? { comment_id: commentId } : undefined,
      },
    );
    return response.data;
  },

  deleteSubtaskAttachment: async (
    changeId: number,
    subtaskId: number,
    attachmentId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/api/changes/${changeId}/subtasks/${subtaskId}/attachments/${attachmentId}`,
    );
    return response.data;
  },

  linkJiraIssue: async (changeId: number, issueKey: string): Promise<unknown> => {
    const response = await api.post(`/api/changes/${changeId}/jira-links`, {
      issue_key: issueKey,
    });
    return response.data;
  },

  refreshJiraLinks: async (changeId: number): Promise<{
    success: boolean;
    refreshed: number;
    items: Array<{
      id: number;
      issue_key: string;
      issue_summary?: string | null;
      issue_status?: string | null;
      issue_url?: string | null;
    }>;
  }> => {
    const response = await api.post(`/api/changes/${changeId}/jira-links/refresh`);
    return response.data;
  },

  removeJiraLink: async (
    changeId: number,
    linkId: number,
  ): Promise<{ success: boolean; removed_issue_key: string }> => {
    const response = await api.delete(`/api/changes/${changeId}/jira-links/${linkId}`);
    return response.data;
  },

  jiraBacklog: async (changeId: number): Promise<{
    issues: Array<{ key: string; summary: string; status: string; jira_browse_url: string }>;
  }> => {
    const response = await api.get(`/api/changes/${changeId}/jira-backlog`);
    return response.data;
  },

  postChangeAssistantSummary: async (changeId: number): Promise<ChangeAssistantResponse> => {
    const response = await api.post(`/api/changes/${changeId}/assistant/summary`);
    return response.data as ChangeAssistantResponse;
  },

  postChangeAssistantAsk: async (
    changeId: number,
    payload: {
      question: string;
      history?: ChangeAssistantHistoryMessage[];
    },
  ): Promise<ChangeAssistantResponse> => {
    const response = await api.post(`/api/changes/${changeId}/assistant/ask`, payload);
    return response.data as ChangeAssistantResponse;
  },
};
